// Centralized environment helpers for frontend
// Ensures consistent API/WS base URLs across app and tests

type AnyEnv = Record<string, string | undefined> | undefined;

function getViteEnv(): AnyEnv {
	// In Vite production builds, import.meta.env is replaced at build time
	// Access it directly rather than through globalThis
	if (typeof import.meta !== 'undefined' && import.meta.env) {
		return import.meta.env as AnyEnv;
	}

	// Fallback to process.env for Node/Jest environment
	// eslint-disable-next-line no-undef
	return (typeof process !== 'undefined' ? (process as any).env : {}) as AnyEnv;
}

function normalizeApiBaseUrl(raw: string | undefined): string {
	const currentOrigin = (typeof window !== 'undefined' && (window as any).location?.origin) ? (window as any).location.origin : '';
	const fallback = currentOrigin ? `${currentOrigin.replace(/\/$/, '')}/api` : '/api';
	if (!raw || typeof raw !== 'string') return fallback;
	// If it already ends with /api (or contains /api path), keep it; else append /api
	try {
		const url = new URL(raw);
		if (url.pathname.endsWith('/api')) return url.toString().replace(/\/$/, '');
		// Append /api to the origin + existing path
		if (url.pathname === '/' || url.pathname === '') {
			url.pathname = '/api';
		} else if (!url.pathname.includes('/api')) {
			url.pathname = url.pathname.replace(/\/$/, '') + '/api';
		}
		return url.toString().replace(/\/$/, '');
	} catch {
		// raw may be a partial path; fallback
		return raw.endsWith('/api') ? raw : `${raw.replace(/\/$/, '')}/api`;
	}
}

function deriveOrigin(apiBaseUrl: string): string {
	try {
		const url = new URL(apiBaseUrl);
		return `${url.protocol}//${url.host}`;
	} catch {
		return (typeof window !== 'undefined' && (window as any).location?.origin) ? (window as any).location.origin : '';
	}
}

const env = getViteEnv() || {};

// Public constants
export const API_BASE_URL: string = normalizeApiBaseUrl((env as any).VITE_API_URL as string | undefined);
export const API_ORIGIN: string = deriveOrigin(API_BASE_URL);
export const WS_BASE_URL: string = ((env as any).VITE_WS_URL as string | undefined) || API_ORIGIN;

// Debug logging (development only)
if (import.meta.env.MODE === 'development') {
	console.debug('[ENV] Environment variables:', {
		raw_env: env,
		VITE_API_URL: (env as any).VITE_API_URL,
	});
	console.debug('[ENV] Resolved URLs:', { API_BASE_URL, API_ORIGIN, WS_BASE_URL });
}

// Helpers for consumers preferring functions
export function getApiBaseUrl(): string {
	return API_BASE_URL;
}

export function getWsBaseUrl(): string {
	return WS_BASE_URL;
}

export function getApiOrigin(): string {
	return API_ORIGIN;
}


